import React, { useState } from "react";

import "../styles/profile.scss";
import { getUserProfile } from "../helper/apicalls/user";
import AddPreferencePopup from "./AddPreferencePopup";

const Preference = ({
  preferences,
  setPreferences,
  profileData,
  onUpdateProfile,
}) => {
  const [showAddPreferencePopup, setShowAddPreferencePopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [isEditingAI, setIsEditingAI] = useState(false);
  const [isEditingTest, setIsEditingTest] = useState(false);
  const [aiPreference, setAiPreference] = useState(
    profileData?.ai_teaching_preference || "easy"
  );
  const [testPreference, setTestPreference] = useState(
    profileData?.test_page_preference || "easy"
  );

  const handleAddPreference = () => {
    setShowAddPreferencePopup(true);
  };

  const handleSavePreference = async (preferenceData) => {
    try {
      setLoading(true);
      const updatedPreferences = [...preferences, preferenceData];

      const updatedProfileData = {
        preference: updatedPreferences,
      };

      await onUpdateProfile(updatedProfileData);

      const refreshResponse = await getUserProfile();
      const refreshedUserData = refreshResponse?.data || refreshResponse;

      setPreferences(refreshedUserData?.preference || []);

      setShowAddPreferencePopup(false);
    } catch (error) {
      console.error("Error adding preference:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClosePopup = () => {
    setShowAddPreferencePopup(false);
  };

  const handleSaveAIPreference = async () => {
    try {
      setLoading(true);
      const updatedData = {
        ai_teaching_preference: aiPreference,
      };

      await onUpdateProfile(updatedData);
      setIsEditingAI(false);
    } catch (error) {
      console.error("Error updating AI preference:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTestPreference = async () => {
    try {
      setLoading(true);
      const updatedData = {
        test_page_preference: testPreference,
      };

      await onUpdateProfile(updatedData);
      setIsEditingTest(false);
    } catch (error) {
      console.error("Error updating test preference:", error);
    } finally {
      setLoading(false);
    }
  };

  const renderGeneralPreferences = () => (
    <div className="preference-content">
      <div className="add-preference-section">
        <button onClick={handleAddPreference} className="add-preference-btn">
          Add Preference
        </button>
      </div>
      {preferences.length > 0 ? (
        <div className="preference-list">
          {preferences.map((pref, index) => (
            <div key={index} className="preference-item">
              <div className="preference-question">
                <div className="question-text">
                  {pref.question}
                </div>
              </div>
              <div className="preference-answer">
                <div className="answer-text">
                  {pref.answer}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="no-preferences">
          <div className="no-preferences-text">
            No preferences added yet. Click "Add Preference" to get started.
          </div>
        </div>
      )}
    </div>
  );

  const renderAITeachingPreferences = () => (
    <div className="preference-content">
      <div className="ai-preference-grid">
        <div className="preference-card">
          <h3>AI Teaching Preference</h3>
          <p>Choose your preferred AI teaching difficulty level</p>
          {isEditingAI ? (
            <div className="preference-edit-container">
              <select
                className="preference-select"
                value={aiPreference}
                onChange={(e) => setAiPreference(e.target.value)}
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
              <div className="edit-buttons">
                <button
                  className="save-preference-btn"
                  onClick={handleSaveAIPreference}
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save"}
                </button>
                <button
                  className="cancel-preference-btn"
                  onClick={() => {
                    setIsEditingAI(false);
                    setAiPreference(
                      profileData?.ai_teaching_preference || "easy"
                    );
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="preference-display-container">
              <div className="preference-value">
                <span className="value-label">Current Preference:</span>
                <span className="value-text">
                  {aiPreference?.charAt(0).toUpperCase() +
                    aiPreference?.slice(1) || "Easy"}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderTestPreferences = () => (
    <div className="preference-content">
      <div className="test-preference-grid">
        <div className="preference-card">
          <h3>Test Difficulty Preference</h3>
          <p>Choose your preferred test difficulty level</p>
          {isEditingTest ? (
            <div className="preference-edit-container">
              <select
                className="preference-select"
                value={testPreference}
                onChange={(e) => setTestPreference(e.target.value)}
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
              <div className="edit-buttons">
                <button
                  className="save-preference-btn"
                  onClick={handleSaveTestPreference}
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save"}
                </button>
                <button
                  className="cancel-preference-btn"
                  onClick={() => {
                    setIsEditingTest(false);
                    setTestPreference(
                      profileData?.test_page_preference || "easy"
                    );
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="preference-display-container">
              <div className="preference-value">
                <span className="value-label">Current Preference:</span>
                <span className="value-text">
                  {testPreference?.charAt(0).toUpperCase() +
                    testPreference?.slice(1) || "Easy"}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="preference-section">
      <div className="preference-tabs">
        <button
          className={`tab-button ${activeTab === "general" ? "active" : ""}`}
          onClick={() => setActiveTab("general")}
        >
          General Preference
        </button>
        <button
          className={`tab-button ${activeTab === "ai" ? "active" : ""}`}
          onClick={() => setActiveTab("ai")}
        >
          AI/Teaching Preference
        </button>
        <button
          className={`tab-button ${activeTab === "test" ? "active" : ""}`}
          onClick={() => setActiveTab("test")}
        >
          Test Preference
        </button>
      </div>

      <div className="tab-content custom-scrollbar">
        {activeTab !== "general" && (
          <div className="add-preference-section">
            <button
              className="add-preference-btn"
              onClick={() => {
                if (activeTab === "ai") {
                  setIsEditingAI(true);
                } else {
                  setIsEditingTest(true);
                }
              }}
            >
              Edit
            </button>
          </div>
        )}
        {activeTab === "general" && renderGeneralPreferences()}
        {activeTab === "ai" && renderAITeachingPreferences()}
        {activeTab === "test" && renderTestPreferences()}
      </div>

      <AddPreferencePopup
        isOpen={showAddPreferencePopup}
        onClose={handleClosePopup}
        onSave={handleSavePreference}
        loading={loading}
      />
    </div>
  );
};

export default Preference;
